import { useState } from 'react';
import type { ListEntry } from '@heybesafe/shared';
import { compareEntries } from '@heybesafe/shared';
import { v4 as uuidv4 } from 'uuid';
import { Icon } from './hbs/Icon';
import { HbsInput } from './hbs/Input';
import { HbsButton } from './hbs/Button';

interface Props {
  /** All entries, INCLUDING tombstones (needed so removals round-trip + merge). */
  entries: ListEntry[];
  /** Logged-in user's id, stamped onto adds/toggles/removals for attribution. */
  currentUserId: string;
  /** Resolve a userId to a display label (name, else email, else a fallback). */
  memberLabelFor: (userId: string) => string;
  /** Hide checked items (the local "show done" toggle lives in the parent). */
  showDone: boolean;
  /** Persist the full next entries array (tombstones included) — add/toggle/remove. */
  onChange: (next: ListEntry[]) => void;
  /** Reorder: place `entryId` between the visible neighbors `prevId`/`nextId`
   *  (either null for an end). The parent runs the shared `moveEntry`. */
  onMove: (entryId: string, prevId: string | null, nextId: string | null) => void;
}

/** Compact relative time: "just now", "5m", "3h", "2d", else a short date. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Collaborative family checklist. Any family member can add, remove, check, and
 * **reorder** items; each shows who added it (hover tooltip). Order is the shared
 * per-entry fractional-index rank — checked items keep their manual slot (no
 * done-sink). Add/toggle/remove produce a new entries array the parent persists;
 * a drag/keyboard reorder calls `onMove`, which the parent resolves through the
 * shared `moveEntry`. Concurrent edits reconcile via the per-entry merge on sync.
 */
export default function ListChecklist({
  entries, currentUserId, memberLabelFor, showDone, onChange, onMove,
}: Props): JSX.Element {
  const [draft, setDraft] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  // The row currently under the pointer during a drag, and whether we'd drop
  // after it (pointer past its vertical midpoint) — drives the drop indicator.
  const [over, setOver] = useState<{ id: string; after: boolean } | null>(null);

  const shown = entries
    .filter((e) => !e.deleted)
    .filter((e) => showDone || !e.done)
    .sort(compareEntries);
  const shownIds = shown.map((e) => e.id);

  function add(): void {
    const text = draft.trim();
    if (!text) return;
    const now = new Date().toISOString();
    const entry: ListEntry = {
      id: uuidv4(), text, addedBy: currentUserId, addedAt: now, done: false,
    };
    onChange([...entries, entry]);
    setDraft('');
  }

  function toggle(id: string): void {
    const now = new Date().toISOString();
    onChange(entries.map((e) =>
      e.id === id && !e.deleted
        ? { ...e, done: !e.done, doneBy: currentUserId, doneAt: now }
        : e,
    ));
  }

  function remove(id: string): void {
    const now = new Date().toISOString();
    onChange(entries.map((e) =>
      e.id === id && !e.deleted
        ? { ...e, deleted: true, deletedBy: currentUserId, deletedAt: now }
        : e,
    ));
  }

  /** Move `id` to `finalIndex` within the visible list and emit the neighbors. */
  function moveToFinalIndex(id: string, finalIndex: number): void {
    const rest = shownIds.filter((x) => x !== id);
    const idx = Math.max(0, Math.min(finalIndex, rest.length));
    const ordered = [...rest.slice(0, idx), id, ...rest.slice(idx)];
    const prev = ordered[idx - 1] ?? null;
    const next = ordered[idx + 1] ?? null;
    if (prev === shownIds[shownIds.indexOf(id) - 1] && next === shownIds[shownIds.indexOf(id) + 1]) {
      return; // no-op (same slot)
    }
    onMove(id, prev, next);
  }

  function handleDrop(targetId: string): void {
    if (!dragId || dragId === targetId) { setDragId(null); setOver(null); return; }
    const rest = shownIds.filter((x) => x !== dragId);
    const base = rest.indexOf(targetId);
    const finalIndex = over?.after ? base + 1 : base;
    moveToFinalIndex(dragId, finalIndex);
    setDragId(null);
    setOver(null);
  }

  /** Keyboard reorder on the drag handle: ArrowUp/ArrowDown move one slot. */
  function handleKeyMove(id: string, ev: React.KeyboardEvent): void {
    if (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return;
    ev.preventDefault();
    const cur = shownIds.indexOf(id);
    if (cur < 0) return;
    moveToFinalIndex(id, ev.key === 'ArrowUp' ? cur - 1 : cur + 1);
  }

  // Hover tooltip: who added it and when, plus who checked it off (when done).
  function attributionTitle(e: ListEntry): string {
    const parts = [`Added by ${memberLabelFor(e.addedBy)} · ${relativeTime(e.addedAt)}`];
    if (e.done && e.doneBy) {
      parts.push(`Checked by ${memberLabelFor(e.doneBy)} · ${relativeTime(e.doneAt ?? e.addedAt)}`);
    }
    return parts.join('\n');
  }

  return (
    <div className="flex flex-col gap-1" data-testid="list-checklist">
      {shown.length === 0 && (
        <p className="text-[13px] text-hbs-text-mute py-2">
          {entries.some((e) => !e.deleted) ? 'No items to show.' : 'No items yet. Add the first one below.'}
        </p>
      )}

      <ul className="flex flex-col">
        {shown.map((e) => (
          <li
            key={e.id}
            draggable
            onDragStart={(ev) => { setDragId(e.id); ev.dataTransfer.effectAllowed = 'move'; }}
            onDragEnd={() => { setDragId(null); setOver(null); }}
            onDragOver={(ev) => {
              if (!dragId) return;
              ev.preventDefault();
              const r = ev.currentTarget.getBoundingClientRect();
              setOver({ id: e.id, after: ev.clientY > r.top + r.height / 2 });
            }}
            onDrop={(ev) => { ev.preventDefault(); handleDrop(e.id); }}
            className={`group flex items-start gap-2 py-2 border-b border-hbs-border last:border-b-0
              ${dragId === e.id ? 'opacity-40' : ''}
              ${over?.id === e.id && dragId && dragId !== e.id
                ? (over.after ? 'shadow-[inset_0_-2px_0_0_var(--hbs-primary)]' : 'shadow-[inset_0_2px_0_0_var(--hbs-primary)]')
                : ''}`}
          >
            <button
              type="button"
              aria-label={`Reorder "${e.text}" (use arrow keys)`}
              id={`list-item-handle-${e.id}`}
              onKeyDown={(ev) => handleKeyMove(e.id, ev)}
              className="mt-0.5 shrink-0 cursor-grab text-hbs-text-mute opacity-0 group-hover:opacity-100 focus:opacity-100 leading-none px-0.5 select-none"
            >
              ⠿
            </button>

            <button
              type="button"
              role="checkbox"
              aria-checked={e.done}
              aria-label={e.done ? `Mark "${e.text}" not done` : `Mark "${e.text}" done`}
              id={`list-item-toggle-${e.id}`}
              onClick={() => toggle(e.id)}
              className={`mt-0.5 w-[18px] h-[18px] shrink-0 rounded-[5px] border flex items-center justify-center
                ${e.done ? 'bg-hbs-primary border-hbs-primary text-hbs-on-primary' : 'border-hbs-border text-transparent hover:border-hbs-primary'}`}
            >
              <Icon name="check" size={12} />
            </button>

            {/* Attribution is shown on hover (title tooltip), not drawn inline. */}
            <div className="flex-1 min-w-0" title={attributionTitle(e)}>
              <span className={`text-[14px] ${e.done ? 'line-through text-hbs-text-mute' : 'text-hbs-text'}`}>
                {e.text}
              </span>
            </div>

            <button
              type="button"
              aria-label={`Remove "${e.text}"`}
              id={`list-item-remove-${e.id}`}
              onClick={() => remove(e.id)}
              className="shrink-0 text-hbs-text-mute opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-hbs-warning text-[16px] leading-none px-1"
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <form
        className="flex gap-2 pt-3"
        onSubmit={(ev) => { ev.preventDefault(); add(); }}
      >
        <div className="flex-1">
          <HbsInput
            label=""
            value={draft}
            onChange={setDraft}
            placeholder="Add an item…"
            name="new-list-item"
          />
        </div>
        <HbsButton
          id="list-add-item-button"
          type="submit"
          disabled={!draft.trim()}
          leadingIcon={<Icon name="plus" size={14} />}
        >
          Add
        </HbsButton>
      </form>
    </div>
  );
}
